import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const InsightsIAPage = () => {
  const { user } = useAuth();
  const [insights, setInsights] = useState([]);
  const [noLeidos, setNoLeidos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadInsights = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    try {
      const res = await api.get(`/asistente/insights/${user.establecimiento_id}`);
      setInsights(res.data.insights || []);
      setNoLeidos(res.data.no_leidos || 0);
    } catch {} finally { setLoading(false); }
  }, [user?.establecimiento_id]);

  useEffect(() => { loadInsights(); }, [loadInsights]);

  const handleGenerarAnalisis = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.post(`/asistente/analisis-automatico/${user.establecimiento_id}`);
      setInsights(prev => [{
        id: res.data.insight_id,
        contenido: res.data.contenido,
        fecha: res.data.fecha,
        leido: false,
        tipo: 'analisis_completo',
      }, ...prev]);
      setExpandedId(res.data.insight_id);
      setNoLeidos(n => n + 1);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar analisis. Reintenta en unos minutos.');
    } finally { setGenerating(false); }
  };

  const handleMarcarLeido = async (insightId) => {
    try {
      await api.put(`/asistente/insights/${insightId}/leer`);
      setInsights(prev => prev.map(i => i.id === insightId ? { ...i, leido: true } : i));
      setNoLeidos(n => Math.max(0, n - 1));
    } catch {}
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    const insight = insights.find(i => i.id === id);
    if (insight && !insight.leido) handleMarcarLeido(id);
  };

  const formatContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h3 key={i} className="text-base font-bold text-gray-900 mt-4 mb-2 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-green-500 rounded-full inline-block" />
          {line.replace('## ', '')}
        </h3>;
      }
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.replace(/^[-*]\s/, '');
        const isUrgent = content.toLowerCase().includes('urgente') || content.toLowerCase().includes('descartar') || content.toLowerCase().includes('inmediato');
        return <p key={i} className={`text-sm pl-4 py-0.5 ${isUrgent ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
          <span className="text-green-500 mr-2">&bull;</span>{content}
        </p>;
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-sm text-gray-700 py-0.5">{line}</p>;
    });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="insights-ia-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-3">
            Analisis IA Automatico
            {noLeidos > 0 && (
              <span className="px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full" data-testid="insights-badge">
                {noLeidos} nuevo{noLeidos > 1 ? 's' : ''}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-1">La IA analiza tu rodeo y te da decisiones concretas con datos</p>
        </div>
        <button
          onClick={handleGenerarAnalisis}
          disabled={generating}
          className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-semibold text-sm hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 transition flex items-center gap-2 shadow-lg"
          data-testid="generar-analisis-btn"
        >
          {generating ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              Analizando rodeo...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Generar Analisis Ahora
            </>
          )}
        </button>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="insight-error">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : insights.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
          <h3 className="text-lg font-bold text-gray-700 mb-2">Sin analisis todavia</h3>
          <p className="text-sm text-gray-500 mb-4">Genera tu primer analisis automatico para ver decisiones y recomendaciones de la IA</p>
          <button onClick={handleGenerarAnalisis} disabled={generating}
            className="px-6 py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 disabled:opacity-50" data-testid="first-analysis-btn">
            Generar Primer Analisis
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {insights.map(insight => (
            <div key={insight.id} className={`bg-white rounded-xl shadow-sm border-2 transition ${!insight.leido ? 'border-green-400' : 'border-gray-100'}`} data-testid={`insight-${insight.id}`}>
              <button
                onClick={() => toggleExpand(insight.id)}
                className="w-full p-4 flex items-center justify-between text-left"
                data-testid={`expand-insight-${insight.id}`}
              >
                <div className="flex items-center gap-3">
                  {!insight.leido && <span className="w-2.5 h-2.5 bg-green-500 rounded-full flex-shrink-0 animate-pulse" />}
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Analisis del Rodeo
                    </p>
                    <p className="text-xs text-gray-500">
                      {insight.fecha ? new Date(insight.fecha).toLocaleString('es-AR') : ''}
                    </p>
                  </div>
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${expandedId === insight.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedId === insight.id && (
                <div className="px-4 pb-5 border-t border-gray-100 pt-3" data-testid={`insight-content-${insight.id}`}>
                  {formatContent(insight.contenido)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InsightsIAPage;
